/**
 * Swiss-specific internationalization and routing utilities
 * Implements Swiss regional codes (de-CH, fr-CH, it-CH, en-CH) and city mappings
 */

export type SwissLanguage = 'de' | 'fr' | 'it' | 'en';
export type SwissRegion = 'de-CH' | 'fr-CH' | 'it-CH' | 'en-CH';

// Language to region mapping for Swiss market
export const languageToRegion: Record<SwissLanguage, SwissRegion> = {
  de: 'de-CH',
  fr: 'fr-CH',
  it: 'it-CH',
  en: 'en-CH'
};

// City mappings per language (SEO-friendly slugs)
export const swissCities = {
  de: {
    'zuerich': 'Zürich',
    'basel': 'Basel',
    'bern': 'Bern',
    'winterthur': 'Winterthur',
    'luzern': 'Luzern',
    'st-gallen': 'St. Gallen',
    'zug': 'Zug'
  },
  fr: {
    'geneve': 'Genève',
    'lausanne': 'Lausanne',
    'zurich': 'Zurich',
    'bale': 'Bâle',
    'berne': 'Berne'
  },
  it: {
    'lugano': 'Lugano',
    'bellinzona': 'Bellinzona'
  },
  en: {
    'zurich': 'Zurich',
    'geneva': 'Geneva',
    'lausanne': 'Lausanne',
    'basel': 'Basel',
    'bern': 'Bern'
  }
} as const;

// Service mappings per language
export const serviceTranslations = {
  de: {
    'ai-video': 'AI-Video',
    'webdesign': 'Webdesign',
    'seo': 'SEO',
    'gbp': 'Google-Business-Profile',
    'marketing-automation': 'Marketing-Automation',
    'ecommerce': 'E-Commerce',
    'branding': 'Branding'
  },
  fr: {
    'ai-video': 'Video-IA',
    'webdesign': 'Webdesign',
    'seo': 'Referencement',
    'gbp': 'Google-Business-Profile',
    'marketing-automation': 'Automatisation-Marketing',
    'ecommerce': 'E-Commerce',
    'branding': 'Image-de-Marque'
  },
  it: {
    'ai-video': 'Video-AI',
    'webdesign': 'Webdesign',
    'seo': 'SEO',
    'gbp': 'Google-Business-Profile',
    'marketing-automation': 'Automazione-Marketing',
    'ecommerce': 'E-Commerce',
    'branding': 'Branding'
  },
  en: {
    'ai-video': 'AI-Video',
    'webdesign': 'Web-Design',
    'seo': 'SEO',
    'gbp': 'Google-Business-Profile',
    'marketing-automation': 'Marketing-Automation',
    'ecommerce': 'E-Commerce',
    'branding': 'Branding'
  }
} as const;

/**
 * Get all available cities for a specific language
 */
export const getCitiesForLanguage = (language: SwissLanguage): string[] => {
  return Object.keys(swissCities[language] || {});
};

/**
 * Get city display name from slug
 */
export const getCityDisplayName = (language: SwissLanguage, citySlug: string): string => {
  return swissCities[language]?.[citySlug] || citySlug;
};

/**
 * Generate all possible service/city combinations for a language
 */
export const generateServiceCityCombinations = (language: SwissLanguage) => {
  const services = Object.keys(serviceTranslations[language]);
  const cities = getCitiesForLanguage(language);
  
  const combinations = [];
  for (const service of services) {
    combinations.push({ service, city: null }); // Service hub page
    for (const city of cities) {
      combinations.push({ service, city }); // Service + city page
    }
  }
  
  return combinations;
};

/**
 * Build service URL path
 */
export const buildServicePath = (language: SwissLanguage, service: string, city?: string): string => {
  const serviceSlug = serviceTranslations[language][service] || service;
  if (city) {
    return `/${language}/${serviceSlug}/${city}`;
  }
  return `/${language}/${serviceSlug}`;
};

/**
 * Swiss cantons mapping for schema areaServed
 */
export const swissCantons = {
  'Zürich': 'ZH',
  'Genève': 'GE',
  'Lausanne': 'VD',
  'Basel': 'BS',
  'Bern': 'BE',
  'Winterthur': 'ZH',
  'Luzern': 'LU',
  'St. Gallen': 'SG',
  'Zug': 'ZG',
  'Lugano': 'TI'
};

/**
 * Get canton code for city
 */
export const getCantonForCity = (cityDisplayName: string): string => {
  return swissCantons[cityDisplayName] || 'CH';
};